import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import ConfirmationModal from '../../components/modals/confirmation';
import OverlayLoader from '../../components/overlayLoader';
import { toLocalDateString } from '../../utils/helper';
import { getStatusLabel } from '../../constants/jobStatus';
import { onMutateSuccess, onQuerySuccess } from '../../utils/queryResponse';
import { deleteNotification, getPaginatedNotifications, markAllNotificationsAsRead, updateNotification } from '../../services/notifications';
import NotificationsFilter from './notificationsFilter';
import Pages from '../../components/pages';
import SetTempEditOrderModal from '../../components/modals/orderEditTemp';
import { SkeletonRows } from '../../components/skeleton';

/* ── helpers ─────────────────────────────────────────────────────────────── */

/** Map a notification message string to a type modifier class */
function getTypeClass(message) {
    if (!message) return 't-info';
    const m = message.toLowerCase();
    if (m.startsWith('low stock: ')) return 't-warn';
    if (m === 'delete request') return 't-amber';
    if (m === 'unpicked job' || m === 'excessive pickup' || m === 'not started cleaning' ||
        m === 'excessive cleaning' || m === 'not started dispatch' || m === 'excessive dispatch') return 't-warn';
    if (m === 'job cancelled') return 't-warn';
    if (m === 'reschedule needed') return 't-warn';
    if (m === 'invoice overdue') return 't-amber';
    if (m === 'job paid') return 't-pay';
    if (m === 'job completed') return 't-done';
    return 't-info';
}

/** Icon SVG by notification type */
function NotifIcon({ message }) {
    const m = (message || '').toLowerCase();

    // delete request — trash
    if (m === 'delete request') return (
        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M3 6h18M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            <line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/>
        </svg>
    );

    // overdue / late — clock
    if (m === 'unpicked job' || m === 'excessive pickup' || m === 'not started dispatch' ||
        m === 'excessive dispatch') return (
        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
        </svg>
    );

    // invoice overdue — banknote
    if (m === 'invoice overdue') return (
        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="3"/><line x1="6" y1="12" x2="6.01" y2="12"/><line x1="18" y1="12" x2="18.01" y2="12"/>
        </svg>
    );

    // cleaning issues — warning triangle
    if (m === 'not started cleaning' || m === 'excessive cleaning') return (
        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
    );

    // assignment — user-check
    if (m === 'job assigned pickup' || m === 'job assigned cleaning' || m === 'job assigned delivery') return (
        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/><polyline points="16 11 18 13 22 9"/>
        </svg>
    );

    // completed — check circle
    if (m === 'job completed' || m === 'job delete approved') return (
        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
        </svg>
    );

    // cancelled — x circle
    if (m === 'job cancelled') return (
        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
    );

    // paid — credit card
    if (m === 'job paid') return (
        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <rect x="1" y="4" width="22" height="16" rx="2"/>
            <line x1="1" y1="10" x2="23" y2="10"/>
        </svg>
    );

    // note — file-text
    if (m === 'job note added') return (
        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
            <polyline points="10 9 9 9 8 9"/>
        </svg>
    );

    // reminder — bell
    if (m === 'reminder due') return (
        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
            <path d="M13.7 21a2 2 0 0 1-3.4 0"/>
        </svg>
    );

    // default — info circle
    return (
        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
    );
}

/** Human-readable label for a notification message value */
function getLabel(value, t) {
    const map = {
        'unpicked job': t('notifications.unpickedJob'),
        'excessive pickup': t('notifications.excessivePickup'),
        'not started cleaning': t('notifications.notStartedCleaning'),
        'excessive cleaning': t('notifications.excessiveCleaning'),
        'not started dispatch': t('notifications.notStartedDispatch'),
        'excessive dispatch': t('notifications.excessiveDispatch'),
        'invoice overdue': t('notifications.invoiceOverdue'),
        'delete request': t('notifications.deleteRequest'),
        'job assigned pickup': t('notifications.jobAssignedPickup'),
        'job assigned cleaning': t('notifications.jobAssignedCleaning'),
        'job assigned delivery': t('notifications.jobAssignedDelivery'),
        'job cancelled': t('notifications.jobCancelled'),
        'job completed': t('notifications.jobCompleted'),
        'job paid': t('notifications.jobPaid'),
        'reschedule needed': t('notifications.rescheduleNeeded'),
        'job note added': t('notifications.jobNoteAdded'),
        'job delete approved': t('notifications.jobDeleteApproved'),
        'reminder due': t('notifications.reminderDue'),
        'client intake received': t('notifications.clientIntakeReceived'),
    };
    if (value?.startsWith('low stock: ')) return t('notificationsPage.lowStockAlert');
    return map[value] || value;
}

/** Render the message body for a notification row */
function NotifBody({ data, onJobClick }) {
    const { t } = useTranslation();
    const { message, objectId, jobId, job } = data;

    // For job-linked notifications the clickable ref is the jobId when available
    // (e.g. 'job note added' stores the note ID in objectId but links via jobId)
    const displayId = jobId || objectId;
    // Filled by <Trans>: the strings wrap the job number as <job>#{{id}}</job>,
    // so each language can put it wherever its word order needs it.
    const jobLink = (
        <span className="text-info" style={{ cursor: 'pointer', fontWeight: 700 }}
            onClick={() => onJobClick(data)} />
    );
    const body = (i18nKey, values = {}) => (
        <Trans
            i18nKey={i18nKey}
            values={{ id: displayId, ...values }}
            components={{
                job: jobLink,
                b: <b />,
                // eslint-disable-next-line jsx-a11y/anchor-has-content -- content comes from the translation
                a: <a href="/clients?tab=pending" style={{ fontWeight: 700 }} />,
            }}
        />
    );

    // The API attaches the job's schedule so an overdue notice can say WHEN
    // the job was due — the notification's own timestamp only says when the
    // notice was raised, which reads as wrong once the job is rescheduled.
    const customerDelivers = job?.pickupType === 'CUSTOMER_DELIVERS';
    const dueAt = customerDelivers ? job?.deliveredByCustomerTime : job?.pickupTime;
    const dueDate = dueAt ? toLocalDateString(dueAt, 'dd.MM.yyyy HH:mm') : '';
    const statusText = job?.status ? getStatusLabel(job.status) : t('jobStatus.SCHEDULED');

    if (message?.startsWith('low stock: ')) return body('notificationsPage.body.lowStock', { name: message.slice('low stock: '.length) });
    if (message === 'unpicked job') {
        const key = customerDelivers
            ? (dueDate ? 'notificationsPage.body.unpickedDropoffAt' : 'notificationsPage.body.unpickedDropoff')
            : (dueDate ? 'notificationsPage.body.unpickedPickupAt' : 'notificationsPage.body.unpickedPickup');
        return body(key, { date: dueDate, status: statusText });
    }
    if (message === 'excessive pickup')      return body('notificationsPage.body.excessivePickup');
    if (message === 'not started cleaning')  return body('notificationsPage.body.notStartedCleaning');
    if (message === 'excessive cleaning')    return body('notificationsPage.body.excessiveCleaning');
    if (message === 'not started dispatch')  return body('notificationsPage.body.notStartedDispatch');
    if (message === 'excessive dispatch')    return body('notificationsPage.body.excessiveDispatch');
    if (message === 'invoice overdue')       return body('notificationsPage.body.invoiceOverdue');
    if (message === 'delete request')        return body('notificationsPage.body.deleteRequest');
    if (message === 'job assigned pickup')   return body('notificationsPage.body.jobAssignedPickup');
    if (message === 'job assigned cleaning') return body('notificationsPage.body.jobAssignedCleaning');
    if (message === 'job assigned delivery') return body('notificationsPage.body.jobAssignedDelivery');
    if (message === 'job cancelled')         return body('notificationsPage.body.jobCancelled');
    if (message === 'job completed')         return body('notificationsPage.body.jobCompleted');
    if (message === 'job paid')              return body('notificationsPage.body.jobPaid');
    if (message === 'reschedule needed')     return body('notificationsPage.body.rescheduleNeeded');
    if (message === 'job note added')        return body('notificationsPage.body.jobNoteAdded');
    if (message === 'job delete approved')   return body('notificationsPage.body.jobDeleteApproved');
    if (message === 'reminder due')          return <>{objectId}</>;
    if (message === 'client intake received') return body('notificationsPage.body.clientIntakeReceived');
    return <>{message}</>;
}

/** Render a notification timestamp as "dd.MM.yyyy   hh:mm AM/PM" with a gap between date and time */
function NotifTimestamp({ date }) {
    const d = new Date(date);
    return (
        <>
            {toLocalDateString(d, 'dd.MM.yyyy')}
            <span style={{ marginLeft: 8 }}>{toLocalDateString(d, 'hh:mm a')}</span>
        </>
    );
}

/* ── page styles ─────────────────────────────────────────────────────────── */

const pageStyles = `
  .sbr .notewrap{max-width:720px;margin:0 auto}
  .sbr .attn.approve{background:linear-gradient(135deg,#FFFBF2,#FEF4E0);border-color:var(--warn-bd);margin-bottom:22px}
  .sbr .attn.approve .ico{border-color:var(--warn-bd);color:var(--warn)}
  .sbr .attn .ameta{font-size:11.5px;font-weight:700;color:var(--faint);margin-top:6px;display:flex;align-items:center;gap:6px}
  .sbr .attn .acts{display:flex;gap:8px;flex-shrink:0}
  .sbr .nfilter{display:flex;align-items:center;gap:10px;margin-bottom:16px}
  .sbr .nsum{font-size:13px;font-weight:600;color:var(--muted)}
  .sbr .nsum b{color:var(--ink);font-weight:800}
  .sbr .note{position:relative;display:flex;gap:14px;align-items:flex-start;background:#fff;border:1px solid var(--line);
    border-radius:var(--r-md);padding:14px 16px;margin-bottom:9px;box-shadow:var(--sh-1);transition:.14s}
  .sbr .note:hover{box-shadow:var(--sh-2);transform:translateY(-1px)}
  .sbr .note.unread{border-color:var(--brand-100);box-shadow:0 1px 2px rgba(16,24,40,.05),inset 3px 0 0 var(--brand)}
  .sbr .note .ntile{width:40px;height:40px;border-radius:12px;flex-shrink:0;display:flex;align-items:center;justify-content:center;background:var(--surface)}
  .sbr .note .ntile .ic{width:19px;height:19px}
  .sbr .note.t-over .ntile{background:var(--bad-bg);color:var(--bad)}
  .sbr .note.t-del .ntile{background:var(--warn-bg);color:var(--warn)}
  .sbr .note.t-pay .ntile{background:var(--good-bg);color:var(--good)}
  .sbr .note.t-done .ntile{background:var(--pickup-bg,var(--brand-50));color:var(--pickup,var(--brand))}
  .sbr .note .nbd{flex:1;min-width:0}
  .sbr .note .nttl{font-size:14px;font-weight:800;color:var(--ink);letter-spacing:-.2px;line-height:1.35}
  .sbr .note .ndet{font-size:12.5px;color:var(--muted);font-weight:500;margin-top:4px;line-height:1.45}
  .sbr .note .ndet b{color:var(--text);font-weight:700}
  .sbr .note .nlk{display:inline-flex;align-items:center;gap:4px;font-size:12.5px;font-weight:800;color:var(--brand);
    margin-top:8px;background:none;border:none;padding:0;cursor:pointer}
  .sbr .note .nlk .ic{width:13px;height:13px}
  .sbr .note .nside{flex-shrink:0;display:flex;flex-direction:column;align-items:flex-end;gap:6px;padding-top:1px}
  .sbr .note .nago{font-size:11.5px;font-weight:700;color:var(--faint);white-space:nowrap}
  .sbr .note .udot{width:8px;height:8px;border-radius:50%;background:var(--brand);flex-shrink:0}
  .sbr .note.read .udot{background:transparent}
`;

/* ── component ───────────────────────────────────────────────────────────── */

const NotificationsPage = () => {
    const { t } = useTranslation();

    const [notifications, setNotifications] = useState([]);
    const [pagination, setPagination] = useState();
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize] = useState(50);
    const [confirmationModal, setConfirmationModal] = useState(false);
    const [editJobModal, setEditJobModal] = useState(false);
    const [selectedJobId, setSelectedJobId] = useState();
    const [isDelete, setIsDelete] = useState(false);
    const [notificationToDelete, setNotificationToDelete] = useState();
    const [filters, setFilters] = useState(['', '']);

    const { data, isLoading } = useQuery(
        ['notifications', 'paginated', pageNumber, pageSize, ...filters],
        () => getPaginatedNotifications(pageNumber, pageSize, ...filters)
    );

    const queryClient = useQueryClient();

    const { mutate, isLoading: isDeleting } = useMutation(deleteNotification, {
        onSuccess: onMutateSuccess((result, id) => {
            if (notifications.length === 1 && pageNumber > 1) {
                setPageNumber(pageNumber - 1);
            }
            queryClient.invalidateQueries(['notifications']);
        }, t('notificationsPage.deletedToast'))
    });

    const { mutate: mutateMarkAsRead, isLoading: isMarkingAsRead } = useMutation(updateNotification, {
        onSuccess: onMutateSuccess(() => {
            queryClient.invalidateQueries(['notifications']);
        })
    });

    const { mutate: mutateMarkAllAsRead, isLoading: isMarkingAllAsRead } = useMutation(markAllNotificationsAsRead, {
        onSuccess: onMutateSuccess(() => {
            queryClient.invalidateQueries(['notifications']);
        })
    });

    useEffect(() => {
        onQuerySuccess(data, (result) => {
            setPagination(result);
            setNotifications(result.data);
        });
    }, [data]);

    const handleShowConfirmation = (notification) => {
        setIsDelete(notification ? true : false);
        setConfirmationModal(true);
        notification && setNotificationToDelete(notification);
    };

    const handleDeleteNotification = () => {
        mutate(notificationToDelete.id);
    };

    const markAsRead = (notification, index) => {
        const _notifications = [...notifications];
        _notifications[index].markingAsRead = true;
        setNotifications(_notifications);
        mutateMarkAsRead({ id: notification.id, isRead: true });
    };

    const handleShowEditJobModal = (data, index) => {
        if (index !== undefined) markAsRead(data, index);
        // For 'job note added' objectId is the note ID; use jobId for navigation.
        setSelectedJobId(data.jobId || data.objectId);
        setEditJobModal(true);
    };

    const unreadCount = notifications.filter(n => !n.isRead).length;

    /* ── render ── */
    return (
        <div className="sbr">
            <style>{pageStyles}</style>
            <ConfirmationModal
                display={confirmationModal}
                toggle={setConfirmationModal}
                title={isDelete ? t('notificationsPage.deleteNotification') : t('notificationsPage.markAllAsRead')}
                message={isDelete ? t('notificationsPage.confirmDeleteMessage') : t('notificationsPage.confirmMarkAllMessage')}
                confirmAction={isDelete ? handleDeleteNotification : mutateMarkAllAsRead}
                confirmText={t('common.confirm')}
                cancelText={t('common.cancel')}
            />
            <SetTempEditOrderModal display={editJobModal} toggle={setEditJobModal} jobId={selectedJobId} />

            {/* ── Desktop layout wrapper ── */}
            <div className="notewrap">

                {/* Page header — desktop style */}
                <div className="dhead">
                    <div>
                        <div className="ttl">{t('notificationsPage.title')}</div>
                        <div className="sub">
                            {unreadCount > 0 ? t('notificationsPage.unread', { count: unreadCount }) : t('notificationsPage.allCaughtUp')}
                        </div>
                    </div>
                    <div className="act">
                        <button
                            className="btn-sbr btn-ghost-sbr btn-sm-sbr"
                            onClick={() => handleShowConfirmation()}
                            disabled={isMarkingAllAsRead}
                        >
                            <svg className="ic" style={{ width: 15, height: 15 }} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <polyline points="20 6 9 17 4 12"/>
                            </svg>
                            {t('notificationsPage.markAllReadBtn')}
                        </button>
                    </div>
                </div>

                {/* Date-filter (collapsible) */}
                <div style={{ marginBottom: '16px' }}>
                    <NotificationsFilter onFilter={setFilters} />
                </div>

                {/* Loading / empty / list */}
                <div style={{ position: 'relative', minHeight: 80 }}>
                    {isLoading && notifications.length === 0 ? (
                        <SkeletonRows rows={6} widths={['14%', '56%', '22%']} />
                    ) : notifications.length === 0 ? (
                        <div className="sbr-empty">
                            <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                                <path d="M13.7 21a2 2 0 0 1-3.4 0"/>
                            </svg>
                            {t('notificationsPage.noNotificationsFound')}
                        </div>
                    ) : (
                        <>
                            {/* Mobile view — .ntf rows */}
                            <div className="sbr-m">
                                {/* mark-all strip */}
                                <div className="ntop">
                                    <button
                                        className="markall"
                                        onClick={() => handleShowConfirmation()}
                                        disabled={isMarkingAllAsRead}
                                    >
                                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                            <polyline points="20 6 9 17 4 12"/>
                                        </svg>
                                        {t('notificationsPage.markAllReadBtn')}
                                    </button>
                                </div>

                                {/* filter chips */}
                                <div className="seg" style={{ marginBottom: 12 }}>
                                    <div className="chip active">
                                        {t('common.all')} <span className="c">{notifications.length}</span>
                                    </div>
                                    {unreadCount > 0 && (
                                        <div className="chip">
                                            {t('notificationsPage.unreadLabel')} <span className="c">{unreadCount}</span>
                                        </div>
                                    )}
                                </div>

                                {notifications.map((notification, index) => {
                                    const typeClass = getTypeClass(notification.message);
                                    const isUnread = !notification.isRead;
                                    const isDeleteReq = notification.message === 'delete request';

                                    return (
                                        <div
                                            key={notification.id}
                                            className={`ntf ${typeClass}${isUnread ? ' unread' : ''}`}
                                        >
                                            {/* icon tile */}
                                            <div className="ntile">
                                                <NotifIcon message={notification.message} />
                                            </div>

                                            {/* body */}
                                            <div className="nbody">
                                                <div className="nhd">
                                                    <span className="nttl">{getLabel(notification.message, t)}</span>
                                                    {isUnread && <span className="udot" />}
                                                </div>
                                                <div className="ndet">
                                                    <NotifBody
                                                        data={notification}
                                                        onJobClick={(d) => handleShowEditJobModal(d, index)}
                                                    />
                                                </div>

                                                {/* delete request inline actions */}
                                                {isDeleteReq && (
                                                    <div className="nact-row">
                                                        <button
                                                            className="primary"
                                                            onClick={() => handleShowEditJobModal(notification, index)}
                                                        >
                                                            {t('notificationsPage.approve')}
                                                        </button>
                                                        <button onClick={() => handleShowEditJobModal(notification, index)}>
                                                            {t('notificationsPage.review')}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>

                                            {/* timestamp + actions */}
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                                                <span className="nwhen" style={{ marginLeft: 0 }}>
                                                    <NotifTimestamp date={notification.updatedAt} />
                                                </span>
                                                <div style={{ display: 'flex', gap: 4 }}>
                                                    {!notification.isRead && (
                                                        <button
                                                            title={t('notificationsPage.markAsRead')}
                                                            style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer', color: 'var(--brand)' }}
                                                            onClick={() => markAsRead(notification, index)}
                                                        >
                                                            {notification.markingAsRead
                                                                ? <OverlayLoader noTextDisplay size="16" />
                                                                : (
                                                                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                                                                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                                                                        <polyline points="22,6 12,13 2,6"/>
                                                                    </svg>
                                                                )
                                                            }
                                                        </button>
                                                    )}
                                                    <button
                                                        title={t('common.delete')}
                                                        style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer', color: '#94A3B8' }}
                                                        onClick={() => handleShowConfirmation(notification)}
                                                    >
                                                        <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <polyline points="3 6 5 6 21 6"/>
                                                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                                                            <path d="M10 11v6"/><path d="M14 11v6"/>
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Desktop view — .note rows */}
                            <div className="sbr-d">
                                {/* urgent delete-request banner — show if any unread delete request exists */}
                                {(() => {
                                    const urgent = notifications.find(n => !n.isRead && n.message === 'delete request');
                                    if (!urgent) return null;
                                    const urgentIndex = notifications.indexOf(urgent);
                                    return (
                                        <div className="attn approve">
                                            <div className="ico">
                                                <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                    <path d="M3 6h18M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                                    <line x1="10" y1="11" x2="10" y2="17"/>
                                                    <line x1="14" y1="11" x2="14" y2="17"/>
                                                </svg>
                                            </div>
                                            <div className="tx">
                                                <b>{t('notificationsPage.approvalNeeded')}</b>
                                                <span>
                                                    <Trans
                                                        i18nKey="notificationsPage.jobDeletionSignOff"
                                                        values={{ id: urgent.objectId }}
                                                        components={{
                                                            job: <span
                                                                style={{ fontWeight: 700, cursor: 'pointer', color: 'var(--warn)' }}
                                                                onClick={() => handleShowEditJobModal(urgent, urgentIndex)}
                                                            />,
                                                        }}
                                                    />
                                                </span>
                                                <div className="ameta">
                                                    <svg className="ic" style={{ width: 12, height: 12, color: 'var(--warn)' }} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                                                    </svg>
                                                    {t('notificationsPage.received')} <NotifTimestamp date={urgent.updatedAt} />
                                                </div>
                                            </div>
                                            <div className="acts" style={{ flexDirection: 'row', gap: 8 }}>
                                                <button
                                                    className="btn-sbr btn-ghost-sbr btn-sm-sbr"
                                                    onClick={() => handleShowEditJobModal(urgent, urgentIndex)}
                                                >
                                                    {t('notificationsPage.review')}
                                                </button>
                                                <button
                                                    className="btn-sbr btn-primary-sbr btn-sm-sbr"
                                                    onClick={() => handleShowEditJobModal(urgent, urgentIndex)}
                                                >
                                                    {t('notificationsPage.approve')}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* filter summary row */}
                                <div className="nfilter">
                                    <div className="nsum">
                                        {t('notificationsPage.notification', { count: notifications.length })}
                                        {unreadCount > 0 && <> · {t('notificationsPage.unread', { count: unreadCount })}</>}
                                    </div>
                                </div>

                                {/* notification list */}
                                {notifications.map((notification, index) => {
                                    const isUnread = !notification.isRead;
                                    const isDeleteReq = notification.message === 'delete request';
                                    const isOverdue = ['unpicked job', 'excessive pickup', 'not started dispatch', 'excessive dispatch',
                                        'not started cleaning', 'excessive cleaning', 'reschedule needed'].includes(notification.message)
                                        || notification.message?.startsWith('low stock: ');
                                    const isPaid = notification.message === 'job paid';
                                    const isCancelled = notification.message === 'job cancelled';

                                    let noteType = 't-done';
                                    if (isDeleteReq) noteType = 't-del';
                                    else if (isOverdue || isCancelled) noteType = 't-over';
                                    else if (isPaid) noteType = 't-pay';

                                    return (
                                        <div
                                            key={notification.id}
                                            className={`note ${noteType}${isUnread ? ' unread' : ' read'}`}
                                        >
                                            {/* icon tile */}
                                            <div className="ntile">
                                                <NotifIcon message={notification.message} />
                                            </div>

                                            {/* body */}
                                            <div className="nbd">
                                                <div className="nttl">{getLabel(notification.message, t)}</div>
                                                <div className="ndet">
                                                    <NotifBody
                                                        data={notification}
                                                        onJobClick={(d) => handleShowEditJobModal(d, index)}
                                                    />
                                                </div>
                                                {notification.object === 'job' && (notification.jobId || notification.objectId) && (
                                                    <button
                                                        className="nlk"
                                                        onClick={() => handleShowEditJobModal(notification, index)}
                                                    >
                                                        {isDeleteReq ? t('notificationsPage.reviewRequest') : t('notificationsPage.viewJob')}
                                                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                            <polyline points="9 18 15 12 9 6"/>
                                                        </svg>
                                                    </button>
                                                )}
                                                {notification.object === 'material' && (
                                                    <a className="nlk" href="/materials">
                                                        {t('notificationsPage.viewMaterials')}
                                                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                            <polyline points="9 18 15 12 9 6"/>
                                                        </svg>
                                                    </a>
                                                )}
                                            </div>

                                            {/* side: unread dot + timestamp + actions */}
                                            <div className="nside">
                                                <span className="udot" />
                                                <span className="nago">
                                                    <NotifTimestamp date={notification.updatedAt} />
                                                </span>
                                                <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
                                                    {!notification.isRead && (
                                                        <button
                                                            title={t('notificationsPage.markAsRead')}
                                                            style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer', color: 'var(--brand)' }}
                                                            onClick={() => markAsRead(notification, index)}
                                                        >
                                                            {notification.markingAsRead
                                                                ? <OverlayLoader noTextDisplay size="16" />
                                                                : (
                                                                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                                                                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                                                                        <polyline points="22,6 12,13 2,6"/>
                                                                    </svg>
                                                                )
                                                            }
                                                        </button>
                                                    )}
                                                    <button
                                                        title={t('common.delete')}
                                                        style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer', color: '#94A3B8' }}
                                                        onClick={() => handleShowConfirmation(notification)}
                                                    >
                                                        <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <polyline points="3 6 5 6 21 6"/>
                                                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                                                            <path d="M10 11v6"/><path d="M14 11v6"/>
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {((isLoading && notifications.length > 0) || isDeleting) && <OverlayLoader />}
                </div>

                {/* Pagination */}
                {pagination?.totalPages > 0 && (
                    <div style={{ paddingBottom: 12, paddingTop: 4 }}>
                        <Pages paginationData={pagination} changePage={setPageNumber} pageNumber={pageNumber} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotificationsPage;
